import './Photo.scss';
import React, { FC, Suspense, lazy, useState } from 'react';
import type { Photo as PhotoFields } from '@/generated/graphql';
import { API_BASE_URI } from '@/store';
import type { ImageSize } from '@/utils';
import {
    USAGE_DEFAULT_SIZE_MAP as usageDefaultSizeMap,
    IMAGE_QUALITY_PROFILES,
} from '@/utils';
import { useUserCurrent } from '@/components/provider/UserCurrentProvider';
import classNames from 'classnames';

const PhotoEdit = lazy(
    () => import(/* webpackChunkName: "edit-main" */ './PhotoEdit'),
);
const EditShell = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/edit/EditShell/EditShell'
        ),
);

type PhotoProps = {
    fields: PhotoFields;
    panelOwnerId?: number;
    imageSize?: ImageSize;
    className?: string;
    noCaption?: boolean;
};

// This is based on 5 max image sizes for large screens, all >= 1200 (or xs: >= 600)
// It assumes the max width of <header><main> and all content will be 1200px
// xl: always 100vw                  : oversized images
// l:  either 100vw, but max 1200px  : full sized
// m:  either 100vw, 50vw, max 600px : regular larger
// s:  either 50vw, 25vw, max 300px  : regular smaller
// xs: either 33vw, 25vw, max 150px  : thumbs
export const usageSizesMap = {
    xl: '100vw',
    l: '(max-width: 1200px) 100vw, 1200px',
    m: '(max-width: 800px) 100vw, (max-width: 1200px) 50vw, 600px',
    s: '(max-width: 800px) 50vw, (max-width: 1200px) 25vw, 300px',
    xs: '(max-width: 450px) 33vw, (max-width: 600px) 25vw, 150px',
};

export const getSrcSet = (url: string, fileChanged: string) => {
    return IMAGE_QUALITY_PROFILES.map((profile) => {
        const cacheBustedUrl = appendCacheBuster(url, fileChanged);
        return `${API_BASE_URI}/files/styles/${profile.drupalMachineName}/public/photos/${cacheBustedUrl} ${profile.width}w`;
    }).join(', ');
};

export const getSrc = (
    url: string,
    fileChanged: string,
    imageSize: ImageSize,
) => {
    const imageSizeDefault = usageDefaultSizeMap[imageSize];
    return `${API_BASE_URI}/files/styles/${imageSizeDefault}/public/photos/${appendCacheBuster(url, fileChanged)}`;
};

// Also replaces .jpg with .webp!
const appendCacheBuster = (fileName, fileChanged) => {
    return `${getWebpUrl(fileName)}?c=${fileChanged}`;
};

const getWebpUrl = (fileName: string) => {
    return fileName.replace(/\.(jpg|jpeg|png)$/i, '.webp');
};

const Photo: FC<PhotoProps> = ({
    fields,
    panelOwnerId,
    imageSize = 'm',
    className,
    noCaption = false,
}) => {
    const { userCurrent } = useUserCurrent();
    const [isLoaded, setIsLoaded] = useState(false);

    if (!fields) {
        console.error('Photo.tsx fields empty panelOwnerId: ', panelOwnerId);
        return;
    }

    let captionContent = null;

    if (!noCaption) {
        const copyrightUrl =
            fields.copyrightUrl || fields.copyright?.copyrightUrl;

        const copyrightClass = classNames('Photo_copyright', {
            'Photo_copyright-left':
                fields.copyright?.copyrightType === 'COPYLEFT',
            'Photo_copyright-public':
                fields.copyright?.copyrightType === 'PUBLIC_DOMAIN',
            'Photo_copyright-isLink': copyrightUrl,
        });

        const copyrightSnippet = copyrightUrl ? (
            <a
                className={copyrightClass}
                href={copyrightUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
            >
                {fields.copyright?.name}
            </a>
        ) : (
            <span className={copyrightClass}>{fields.copyright?.name}</span>
        );

        captionContent = (
            <figcaption className="Photo_caption">
                <span className="Photo_captionText">
                    {fields.caption} &mdash;&nbsp;
                </span>
                {copyrightSnippet}
            </figcaption>
        );
    }

    const photoContent = (
        <figure className={classNames('Photo', className)}>
            <img
                className={classNames('Photo_img', {
                    isLoaded: isLoaded,
                })}
                src={getSrc(fields.url, fields.fileChanged, imageSize)}
                srcSet={getSrcSet(fields.url, fields.fileChanged)}
                sizes={usageSizesMap[imageSize]}
                width={fields.w}
                height={fields.h}
                loading="lazy"
                alt={fields.caption}
                onLoad={() => setIsLoaded(true)}
            />
            {captionContent}
        </figure>
    );

    if (userCurrent?.isEditMode === 'MEDIA' && panelOwnerId) {
        const passEditState = {
            editKey: fields.id,
            componentType: 'PHOTO',
            fields: {},
            localFields: {
                caption: fields.caption,
                copyright: fields.copyright?.id,
                copyrightUrl: fields.copyrightUrl,
                imageFile: null,
            },
            ownerId: panelOwnerId,
        };

        return (
            <Suspense fallback={photoContent}>
                <EditShell passEditState={passEditState}>
                    <PhotoEdit photoId={fields.id} existingPhotoFields={fields}>
                        {photoContent}
                    </PhotoEdit>
                </EditShell>
            </Suspense>
        );
    }

    return photoContent;
};

export default Photo;
